import { createContext, useContext } from 'react'
import Axios from 'axios'
import jalali from 'jalaali-js'
import { ApexOptions } from 'apexcharts'

import { strings } from 'strings'
import { store } from 'store/store'

export const PERSIAN_WEEKDAYS = [
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنج‌شنبه',
  'جمعه',
  'شنبه',
]

export const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

export function toPersianDigits(s: string | number): string {
  return String(s).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)])
}

// Sentinel "all data" lower bound. Matches the EPOCH_ISO used elsewhere.
export const EPOCH_DAY_KEY = '2000-01-01'
export const EPOCH_ISO = '2000-01-01T00:00:00Z'

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function tehranParts(now: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
  }
}

export function tehranTodayKey(): string {
  const { y, m, d } = tehranParts(new Date())
  return `${y}-${pad2(m)}-${pad2(d)}`
}

export function tehranDayKeyOf(date: Date): string {
  const { y, m, d } = tehranParts(date)
  return `${y}-${pad2(m)}-${pad2(d)}`
}

export function shiftDayKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(
    dt.getUTCDate(),
  )}`
}

function dayKeyDiff(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  const ms = Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)
  return Math.round(ms / 86400000)
}

export type BucketGranularity = 'day' | 'hour'

export interface BucketSpec {
  from: string
  to: string
  keys: string[]
  granularity: BucketGranularity
}

// Build a daily-bucketed spec spanning [fromKey, toKey] inclusive. Caller is
// responsible for ordering (from <= to); if reversed, we swap so the chart
// still renders something sensible.
export function computeSpecFromKeys(
  fromKey: string,
  toKey: string,
): BucketSpec {
  let from = fromKey
  let to = toKey
  if (dayKeyDiff(from, to) < 0) {
    const tmp = from
    from = to
    to = tmp
  }
  const days = dayKeyDiff(from, to) + 1
  const keys: string[] = []
  for (let i = 0; i < days; i++) keys.push(shiftDayKey(from, i))
  return { from, to, keys, granularity: 'day' }
}

// Single-day hourly spec — produces 24 keys formatted to match the
// backend's `to_char(... 'YYYY-MM-DD HH24:00')` output (e.g. "2026-05-05 14:00").
// Used when the page-level range collapses to a single Tehran day.
export function computeHourlySpecForDay(dayKey: string): BucketSpec {
  const keys: string[] = []
  for (let h = 0; h < 24; h++) {
    keys.push(`${dayKey} ${pad2(h)}:00`)
  }
  return { from: dayKey, to: dayKey, keys, granularity: 'hour' }
}

// "HH:00" in Persian digits. Source key is "YYYY-MM-DD HH:00".
export function formatHourLabel(hourKey: string): string {
  const hh = hourKey.slice(11, 13)
  return `${toPersianDigits(hh)}:۰۰`
}

export function formatDayLabel(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const weekday = PERSIAN_WEEKDAYS[dt.getUTCDay()]
  const j = jalali.toJalaali(y, m, d)
  const month = PERSIAN_MONTHS[j.jm - 1]
  return `${weekday} ${toPersianDigits(j.jd)} ${month}`
}

// Format a unix timestamp (seconds) as Jalali "YYYY/MM/DD,HH:mm" in Tehran
// time, in Persian digits.
//
// Persian-Indic digits are classified as Arabic Numbers (AN) by the Unicode
// bidi algorithm and reorder against neutrals like ',' or '/' when placed in
// an RTL container, even when the host element has `direction: ltr` — that's
// what made "DD/MM/YYYY, HH:mm" render visually as "HH:mm, DD/MM/YYYY". The
// LRI / PDI pair (U+2066 … U+2069) wraps the value in a directional isolate
// so the run is treated as LTR regardless of the surrounding paragraph
// direction; the isolate marks themselves are zero-width and invisible.
const tehranDateTimeParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tehran',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function formatJalaliDateTime(unixSeconds: number): string {
  if (!unixSeconds || unixSeconds <= 0) return '—'
  const parts = tehranDateTimeParts.formatToParts(unixSeconds * 1000)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  const gy = Number(get('year'))
  const gm = Number(get('month'))
  const gd = Number(get('day'))
  const hour = get('hour').padStart(2, '0')
  const minute = get('minute').padStart(2, '0')
  const j = jalali.toJalaali(gy, gm, gd)
  const text = toPersianDigits(
    `${j.jy}/${pad2(j.jm)}/${pad2(j.jd)},${hour}:${minute}`,
  )
  return `⁦${text}⁩`
}

export function formatDurationBreakdown(seconds: number): string {
  if (!seconds || seconds <= 0) return '—'
  const total = Math.round(seconds)
  const days = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const parts: string[] = []
  if (days) parts.push(`${days}d`)
  if (h) parts.push(`${h}h`)
  if (m) parts.push(`${m}m`)
  if (s && !days) parts.push(`${s}s`)
  return parts.length ? parts.join(' ') : '0s'
}

export function authHeader() {
  const token =
    store.getState().auth.token ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')
  return { Authorization: `Bearer ${token}` }
}

export async function fetchJson<T>(
  endpoint: string,
  from: string,
  to: string,
  granularity?: BucketGranularity,
): Promise<T> {
  const params: Record<string, string> = { from, to }
  if (granularity === 'hour') params.granularity = 'hour'
  const res = await Axios.get(endpoint, {
    params,
    headers: authHeader(),
  })
  return res.data as T
}

// Page-wide date range driving every analytics chart.
export interface AnalyticsRangeValue {
  // dayKey form (YYYY-MM-DD) — the chart bucket boundaries and SWR keys.
  fromKey: string
  toKey: string
  // True when the user picked "all data" — charts treat the range as the
  // full dataset and (where relevant) swap window-scoped fetches for
  // EPOCH-anchored ones.
  isAllData: boolean
  // Daily bucket spec for the active range.
  bucketSpec: BucketSpec
  // Stable ISO timestamp shared by every chart that needs an "all-time" upper
  // bound. Hoisting this prevents per-component `new Date().toISOString()`
  // calls from generating slightly different SWR keys and duplicating fetches.
  allTimeToIso: string
}

export const AnalyticsRangeContext =
  createContext<AnalyticsRangeValue | null>(null)

export function useAnalyticsRange(): AnalyticsRangeValue {
  const ctx = useContext(AnalyticsRangeContext)
  if (!ctx) {
    throw new Error(
      'useAnalyticsRange must be used inside AnalyticsRangeContext.Provider',
    )
  }
  return ctx
}

// When the global range collapses to a single day, line charts degrade to a
// single point — these helpers render a stacked-or-grouped bar instead.
export interface SingleDayBarSeries {
  name: string
  color: string
  value: number
  // Per-bucket "new" count. Optional so existing callers that don't track
  // it (yet) still compile; when present, surfaced in tooltip and labels
  // so the single-day view matches the multi-day "new alongside total"
  // semantics.
  newCount?: number
  // True if clicking this bar should open the per-bucket dialog. Mirrors the
  // line-chart marker gating: growth shows new entities (newCount > 0),
  // activity shows active entities (activeCount > 0).
  clickable: boolean
}

export interface SingleDayBarInput {
  categoryLabel: string
  yAxisTitle: string
  series: SingleDayBarSeries[]
  // Stacked when series partition the universe (e.g. devices + bases).
  // Grouped when series overlap (e.g. Total + Raymand + NonRaymand, where
  // Total ≈ Raymand + NonRaymand and stacking would double-count).
  stacked: boolean
  onBarClick?: (seriesIndex: number) => void
}

export function singleDayBarOptions(input: SingleDayBarInput): ApexOptions {
  const { categoryLabel, yAxisTitle, series, stacked, onBarClick } = input
  const anyNew = series.some((s) => (s.newCount ?? 0) > 0)
  return {
    series: series.map((s) => ({ name: s.name, data: [s.value] })),
    chart: {
      type: 'bar',
      stacked,
      fontFamily: 'vazir',
      height: 360,
      animations: { enabled: false },
      toolbar: { show: true },
      events: {
        dataPointSelection: (
          _e,
          _ctx,
          cfg: { seriesIndex?: number; dataPointIndex?: number },
        ) => {
          if (!onBarClick) return
          const si = cfg?.seriesIndex ?? -1
          const s = series[si]
          if (!s || !s.clickable) return
          onBarClick(si)
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: stacked ? '40%' : '60%',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) =>
        (val as number) > 0 ? String(Math.round(val as number)) : '',
      offsetY: -16,
      style: { fontSize: '11px', colors: ['#333'] },
      background: { enabled: false },
    },
    xaxis: {
      categories: [categoryLabel],
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      title: { text: yAxisTitle },
      labels: { formatter: (val) => String(Math.round(val as number)) },
    },
    legend: { position: 'top', horizontalAlign: 'left' },
    colors: series.map((s) => s.color),
    tooltip: anyNew
      ? newAndTotalTooltip(
          series.map((s) => ({
            name: s.name,
            color: s.color,
            total: [s.value],
            newCount: [s.newCount ?? 0],
          })),
        )
      : {
          shared: false,
          y: { formatter: (val) => String(Math.round(val as number)) },
        },
  }
}

// strings reference kept here so old callers don't break while we migrate.
export { strings as analyticsStrings }

// Shared "total + new in bucket" tooltip used by every line chart so that
// every chart on the analytics page shows the per-bucket new count alongside
// the running/active total — same format as the original GrowthChart.
export interface NewAndTotalSeries {
  name: string
  color: string
  total: number[]
  newCount: number[]
}

export function newAndTotalTooltip(
  series: NewAndTotalSeries[],
): NonNullable<ApexOptions['tooltip']> {
  return {
    shared: true,
    // ApexCharts throws when shared:true and intersect:true coexist; bar
    // charts default to intersect:true, so this must be explicit.
    intersect: false,
    custom: ({
      dataPointIndex,
      w,
    }: {
      dataPointIndex: number
      w: {
        globals: {
          categoryLabels?: string[]
          labels?: string[]
          collapsedSeriesIndices?: number[]
        }
      }
    }) => {
      const title =
        w.globals.categoryLabels?.[dataPointIndex] ??
        w.globals.labels?.[dataPointIndex] ??
        ''
      const collapsed = w.globals.collapsedSeriesIndices ?? []
      const rows = series
        .map((s, si) => {
          if (collapsed.indexOf(si) !== -1) return ''
          const total = s.total[dataPointIndex] ?? 0
          const n = s.newCount[dataPointIndex] ?? 0
          const newRow =
            n > 0
              ? `<div class="apexcharts-tooltip-y-group">` +
                `<span class="apexcharts-tooltip-text-y-label">${strings.newInBucket}: </span>` +
                `<span class="apexcharts-tooltip-text-y-value">+${n}</span>` +
                `</div>`
              : ''
          return (
            `<div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex;align-items:flex-start;padding:4px 8px;">` +
            `<span class="apexcharts-tooltip-marker" style="background-color:${s.color};margin-top:6px;"></span>` +
            `<div class="apexcharts-tooltip-text" style="font-family:vazir;">` +
            `<div class="apexcharts-tooltip-y-group">` +
            `<span class="apexcharts-tooltip-text-y-label">${s.name}: </span>` +
            `<span class="apexcharts-tooltip-text-y-value">${total}</span>` +
            `</div>` +
            newRow +
            `</div>` +
            `</div>`
          )
        })
        .join('')
      return (
        `<div class="apexcharts-tooltip-title" style="font-family:vazir;font-size:12px;">${title}</div>` +
        rows
      )
    },
  }
}
